import { useState, useRef, useEffect } from 'react';
import { KonvaCanvas } from '../../core';
import { ViewportTransform } from '../../core/types';
import { CreateMode } from '../../editing/types';
import { useTextEditor } from '../../editing/useTextEditor';
import { useSelection } from '../../editing/SelectionContext';
import {
  SelectionManager,
  DragManager,
  CreationManager,
  ConnectorManager,
  HistoryManager,
  ClipboardManager,
  GroupingManager,
  LayerOrderManager,
  AnchorManager,
} from '../../managers';
import { EventEmitter } from '../../core/EventEmitter';
import { EditableElement } from '../../editing/types';
import { CanvasMode } from '../../BoardCanvas.types';

export interface UseCanvasStateReturn {
  // Refs
  containerRef: React.RefObject<HTMLDivElement>;
  canvasRef: React.MutableRefObject<KonvaCanvas | null>;
  emitterRef: React.MutableRefObject<EventEmitter>;
  selectionManagerRef: React.MutableRefObject<SelectionManager | null>;
  dragManagerRef: React.MutableRefObject<DragManager | null>;
  creationManagerRef: React.MutableRefObject<CreationManager | null>;
  connectorManagerRef: React.MutableRefObject<ConnectorManager | null>;
  historyManagerRef: React.MutableRefObject<HistoryManager | null>;
  clipboardManagerRef: React.MutableRefObject<ClipboardManager | null>;
  groupingManagerRef: React.MutableRefObject<GroupingManager | null>;
  layerOrderManagerRef: React.MutableRefObject<LayerOrderManager | null>;
  anchorManagerRef: React.MutableRefObject<AnchorManager | null>;
  elementsRef: React.MutableRefObject<EditableElement[]>;
  modeRef: React.MutableRefObject<CanvasMode>;
  createModeRef: React.MutableRefObject<CreateMode>;
  connectorModeRef: React.MutableRefObject<'straight' | 'bent' | 'curved' | null>;
  clearSelectionRef: React.MutableRefObject<() => void>;
  renderedIdsRef: React.MutableRefObject<Set<string>>;
  
  // State
  selectedIds: string[];
  clearSelection: () => void;
  selectElement: (id: string, multiSelect?: boolean) => void;
  selectElements?: (ids: string[], replace?: boolean) => void;
  mode: CanvasMode;
  setMode: React.Dispatch<React.SetStateAction<CanvasMode>>;
  elements: EditableElement[];
  setElements: React.Dispatch<React.SetStateAction<EditableElement[]>>;
  contextMenu: { x: number; y: number; selectedIds: string[] } | null;
  setContextMenu: React.Dispatch<React.SetStateAction<{ x: number; y: number; selectedIds: string[] } | null>>;
  zoomControls: { scale: number; zoomIn: () => void; zoomOut: () => void; fullscreen: () => void } | null;
  setZoomControls: React.Dispatch<React.SetStateAction<{ scale: number; zoomIn: () => void; zoomOut: () => void; fullscreen: () => void } | null>>;
  stageTransform: ViewportTransform;
  setStageTransform: React.Dispatch<React.SetStateAction<ViewportTransform>>;
  connectorMode: 'straight' | 'bent' | 'curved' | null;
  setConnectorMode: React.Dispatch<React.SetStateAction<'straight' | 'bent' | 'curved' | null>>;
  createMode: CreateMode;
  setCreateMode: React.Dispatch<React.SetStateAction<CreateMode>>;
  isCreating: boolean;
  setIsCreating: React.Dispatch<React.SetStateAction<boolean>>;
  isTransforming: boolean;
  setIsTransforming: React.Dispatch<React.SetStateAction<boolean>>;
  isEditMode: boolean;
  textEditor: ReturnType<typeof useTextEditor>;
}

export function useCanvasState(
  initialMode: CanvasMode,
  initialElements: EditableElement[]
): UseCanvasStateReturn {
  // Container ref for Konva canvas
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Canvas and manager refs (mutable so we can assign in initialization)
  const canvasRef = useRef<KonvaCanvas | null>(null);
  const emitterRef = useRef<EventEmitter>(new EventEmitter());
  const selectionManagerRef = useRef<SelectionManager | null>(null);
  const dragManagerRef = useRef<DragManager | null>(null);
  const creationManagerRef = useRef<CreationManager | null>(null);
  const connectorManagerRef = useRef<ConnectorManager | null>(null);
  const historyManagerRef = useRef<HistoryManager | null>(null);
  const clipboardManagerRef = useRef<ClipboardManager | null>(null);
  const groupingManagerRef = useRef<GroupingManager | null>(null);
  const layerOrderManagerRef = useRef<LayerOrderManager | null>(null);
  const anchorManagerRef = useRef<AnchorManager | null>(null);
  
  // React state
  const { selectedIds, clearSelection, selectElement, selectElements } = useSelection();
  const [mode, setMode] = useState<CanvasMode>(initialMode);
  const [elements, setElements] = useState<EditableElement[]>(initialElements);

  // Ref to track elements without causing re-renders (for drag updates)
  const elementsRef = useRef<EditableElement[]>(initialElements);
  
  // Track rendered element IDs to avoid duplicate rendering
  const renderedIdsRef = useRef<Set<string>>(new Set());

  // Ref for clearSelection to use in event handlers (avoids stale closures)
  const clearSelectionRef = useRef(clearSelection);
  useEffect(() => {
    clearSelectionRef.current = clearSelection;
  }, [clearSelection]);

  // Keep elementsRef in sync with elements state
  useEffect(() => {
    elementsRef.current = elements;
  }, [elements]);
  
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; selectedIds: string[] } | null>(null);
  const [zoomControls, setZoomControls] = useState<{ scale: number; zoomIn: () => void; zoomOut: () => void; fullscreen: () => void } | null>(null);
  const [stageTransform, setStageTransform] = useState<ViewportTransform>({ scale: 0.5, x: 0, y: 0 });
  const [connectorMode, setConnectorMode] = useState<'straight' | 'bent' | 'curved' | null>(null);
  const [createMode, setCreateMode] = useState<CreateMode>({ type: 'select' });
  const [isCreating, setIsCreating] = useState(false);
  const [isTransforming, setIsTransforming] = useState(false);
  
  const isEditMode = mode === 'edit';
  
  // Ref to track current mode for event handlers
  const modeRef = useRef<CanvasMode>(mode);
  useEffect(() => {
    modeRef.current = mode;
  }, [mode]);
  
  // Ref to track current create mode for event handlers
  const createModeRef = useRef<CreateMode>(createMode);
  useEffect(() => {
    createModeRef.current = createMode;
    
    // Update panning state based on create mode
    // Pan mode allows left-click panning, select mode disables it for marquee
    if (canvasRef.current) {
      canvasRef.current.setPanningDisabled(createMode.type !== 'pan');
    }
    
    // Update CreationManager mode when createMode changes (from keyboard shortcuts or toolbar)
    if (creationManagerRef.current) {
      creationManagerRef.current.setMode(createMode);
      
      // Show cursor preview if mouse is over canvas and in create mode
      if (canvasRef.current && createMode.type !== 'select' && createMode.type !== 'pan') {
        const stage = canvasRef.current.getStage();
        if (stage) {
          const pointerPos = stage.getPointerPosition();
          if (pointerPos) {
            const pos = canvasRef.current.screenToLayer(pointerPos);
            creationManagerRef.current.showCursorPreview(pos.x, pos.y);
          }
        }
      }
    }
  }, [createMode]);
  
  // Ref to track connector mode for event handlers
  const connectorModeRef = useRef<'straight' | 'bent' | 'curved' | null>(connectorMode);
  useEffect(() => {
    connectorModeRef.current = connectorMode;
  }, [connectorMode]);

  // Text editing - setup callback that uses refs to access current state
  const elementsRefForText = useRef<EditableElement[]>(initialElements);
  
  // Update elements ref when elements change
  useEffect(() => {
    elementsRefForText.current = elements;
  }, [elements]);
  
  // Text editor with callback that uses refs (historyManagerRef will be set in initialization)
  const textEditor = useTextEditor({
    onTextChange: (id: string, content: string, style: any) => {
      // Use historyManagerRef directly - it will be set during initialization
      if (!historyManagerRef.current) {
        console.warn('HistoryManager not initialized yet, cannot save text changes');
        return;
      }
      
      const element = elementsRefForText.current.find((el) => el.id === id);
      if (!element) {
        console.warn(`Element ${id} not found, cannot save text changes`);
        return;
      }
      
      const isShape = element.type === 'shape';
      
      const updates = {
        data: {
          ...element.data,
          text: content,
          fontSize: style.fontSize,
          fontFamily: style.fontFamily,
          fontStyle: style.fontStyle,
          ...(isShape ? { textFill: style.fill } : { fill: style.fill }),
          align: style.align,
          lineHeight: style.lineHeight,
          letterSpacing: style.letterSpacing,
          textStyle: style,
        },
      };
      
      historyManagerRef.current.modifyElement(id, updates);
    },
  });

  return {
    // Refs
    containerRef,
    canvasRef,
    emitterRef,
    selectionManagerRef,
    dragManagerRef,
    creationManagerRef,
    connectorManagerRef,
    historyManagerRef,
    clipboardManagerRef,
    groupingManagerRef,
    layerOrderManagerRef,
    anchorManagerRef,
    elementsRef,
    modeRef,
    createModeRef,
    connectorModeRef,
    clearSelectionRef,
    renderedIdsRef,
    
    // State
    selectedIds,
    clearSelection,
    selectElement,
    selectElements,
    mode,
    setMode,
    elements,
    setElements,
    contextMenu,
    setContextMenu,
    zoomControls,
    setZoomControls,
    stageTransform,
    setStageTransform,
    connectorMode,
    setConnectorMode,
    createMode,
    setCreateMode,
    isCreating,
    setIsCreating,
    isTransforming,
    setIsTransforming,
    isEditMode,
    textEditor,
  };
}
